import { DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  InputNumber,
  List,
  Progress,
  Skeleton,
  Statistic,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import type { InputRef } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  approveStockCount,
  cancelStockCount,
  exportAutocountDifferencesCsv,
  getAutocountDifferences,
  getStockCount,
  putStockCountLine,
  removeStockCountLine,
  sendBackStockCount,
  submitStockCount,
  type StockCountLineView,
  type StockCountView,
} from './counts-api';

const { Text, Title } = Typography;

const STATUS_COLOUR: Record<StockCountView['status'], string> = {
  COUNTING: 'blue',
  SUBMITTED: 'gold',
  APPROVED: 'green',
  CANCELLED: 'default',
};

function hasDifference(l: StockCountLineView): boolean {
  return l.difference !== undefined && l.difference !== null && Number(l.difference) !== 0;
}

/** One stock count: the blind counting screen for counters, the differences screen for managers. */
export function StockCountPage(): ReactNode {
  const { t } = useTranslation();
  const { uid = '' } = useParams();
  const queryClient = useQueryClient();
  const key = ['stock-count', uid];

  const count = useQuery({ queryKey: key, queryFn: () => getStockCount(uid) });

  const store = (next: StockCountView): void => {
    queryClient.setQueryData(key, next);
    void queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
  };

  if (count.isPending) return <Skeleton active />;
  if (count.isError) return <Alert type="error" showIcon message={t('counts.loadFailedOne')} />;

  const view = count.data;
  return view.status === 'COUNTING' && view.canCount ? (
    <CountingMode view={view} onChange={store} />
  ) : (
    <ReviewMode view={view} onChange={store} />
  );
}

interface ModeProps {
  view: StockCountView;
  onChange: (next: StockCountView) => void;
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** Cancel with a required reason; shared by both modes. */
function CancelDrawer({ view, open, onClose, onChange }: ModeProps & { open: boolean; onClose: () => void }): ReactNode {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: () => cancelStockCount(view.uid, { reason: reason.trim() }),
    onSuccess: (next) => {
      setReason('');
      onClose();
      onChange(next);
    },
  });
  return (
    <ActionDrawer
      open={open}
      title={t('counts.cancelTitle')}
      submitLabel={t('counts.cancelTitle')}
      danger
      consequence={t('counts.cancelConsequence')}
      onClose={onClose}
      onSubmit={() => {
        cancel.mutate();
      }}
      submitting={cancel.isPending}
      submitDisabled={reason.trim().length === 0}
      error={
        cancel.isError ? (
          <Alert type="error" showIcon message={errorText(cancel.error, t('counts.actionFailed'))} />
        ) : undefined
      }
    >
      <label htmlFor="count-cancel-reason">{t('counts.reason')}</label>
      <Input.TextArea
        id="count-cancel-reason"
        rows={3}
        value={reason}
        placeholder={t('counts.reasonRequired')}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
    </ActionDrawer>
  );
}

function CountingMode({ view, onChange }: ModeProps): ReactNode {
  const { t } = useTranslation();
  const codeRef = useRef<InputRef>(null);
  const qtyRef = useRef<React.ComponentRef<typeof InputNumber>>(null);
  const [code, setCode] = useState('');
  const [qty, setQty] = useState<string | null>(null);
  const [step, setStep] = useState<'code' | 'qty'>('code');
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (step === 'qty') {
      // A prefilled figure is selected, so typing replaces it rather than appending to it.
      qtyRef.current?.focus({ cursor: 'all' });
    } else codeRef.current?.focus();
  }, [step]);

  const done = (next: StockCountView): void => {
    setCode('');
    setQty(null);
    setStep('code');
    onChange(next);
  };

  const put = useMutation({
    mutationFn: (input: { code: string; countedQty: string }) => putStockCountLine(view.uid, input),
    onSuccess: (next) => {
      setError(null);
      done(next);
    },
    onError: (e: unknown) => {
      setError(errorText(e, t('counts.actionFailed')));
      setCode('');
      setQty(null);
      setStep('code');
    },
  });

  const remove = useMutation({
    mutationFn: (lineUid: string) => removeStockCountLine(view.uid, lineUid),
    onSuccess: (next) => {
      setError(null);
      done(next);
      codeRef.current?.focus();
    },
    onError: (e: unknown) => {
      setError(errorText(e, t('counts.actionFailed')));
      codeRef.current?.focus();
    },
  });

  const submit = useMutation({
    mutationFn: () => submitStockCount(view.uid, {}),
    onSuccess: (next) => {
      setError(null);
      setMissing([]);
      onChange(next);
    },
    onError: (e: unknown) => {
      setError(errorText(e, t('counts.actionFailed')));
      const codes = e instanceof ApiError ? e.details['skuCodes'] : undefined;
      setMissing(Array.isArray(codes) ? codes.filter((c): c is string => typeof c === 'string') : []);
      codeRef.current?.focus();
    },
  });

  const toQty = (): void => {
    const value = code.trim();
    if (value.length === 0) return;
    const wanted = value.toLowerCase();
    const existing = view.lines.find((l) => l.skuCode.toLowerCase() === wanted || l.barcode?.toLowerCase() === wanted);
    setQty(existing?.countedQty ?? null);
    setError(null);
    setStep('qty');
  };

  // Enter flushes the typed text in the same event that fires onPressEnter, so the
  // state has not caught up yet: read the figure from the field itself.
  const save = (typed: string | null = qty): void => {
    const figure = typed?.trim() ?? '';
    if (figure.length === 0 || put.isPending) return;
    put.mutate({ code: code.trim(), countedQty: figure });
  };

  const toCount = view.lines.filter((l) => l.countedQty === null);
  const counted = view.lines.filter((l) => l.countedQty !== null);
  const percent =
    view.progress.total === 0 ? 0 : Math.round((view.progress.counted / view.progress.total) * 100);

  return (
    <Flex vertical gap={16}>
      <Flex vertical gap={4}>
        <Title level={4} className="tabular" style={{ margin: 0 }}>
          {view.countNo}
        </Title>
        <Text type="secondary">{view.locationName}</Text>
      </Flex>

      <Card>
        <Flex vertical gap={12}>
          <Text strong>{t('counts.progress', view.progress)}</Text>
          <Progress percent={percent} showInfo={false} />
          <Text type="secondary">{t('counts.putAside')}</Text>

          {step === 'code' ? (
            <Input
              ref={codeRef}
              aria-label={t('counts.codeLabel')}
              className="tabular"
              style={{ fontSize: 20, height: 56 }}
              size="large"
              autoComplete="off"
              inputMode="text"
              value={code}
              placeholder={t('counts.codePlaceholder')}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              onPressEnter={toQty}
            />
          ) : (
            <Flex vertical gap={8}>
              <Text strong className="tabular">
                {t('counts.counting', { code: code.trim() })}
              </Text>
              <InputNumber
                ref={qtyRef}
                aria-label={t('counts.qtyLabel')}
                inputMode="decimal"
                stringMode
                min="0"
                controls={false}
                style={{ width: '100%', fontSize: 28 }}
                size="large"
                value={qty}
                onChange={(v) => {
                  setQty(v);
                }}
                onPressEnter={(e) => {
                  save(e.currentTarget.value);
                }}
              />
              <Button type="primary" size="large" block style={{ height: 52 }} loading={put.isPending} disabled={qty === null || qty.length === 0} onClick={() => {
                  save();
                }}
              >
                {t('counts.saveQty')}
              </Button>
            </Flex>
          )}
        </Flex>
      </Card>

      {error !== null && (
        <Alert
          type="error"
          showIcon
          message={error}
          description={
            missing.length > 0 ? (
              <Flex vertical gap={4}>
                <Text>{t('counts.incompleteList')}</Text>
                <Flex gap={4} wrap>
                  {missing.map((c) => (
                    <Tag key={c} className="tabular">
                      {c}
                    </Tag>
                  ))}
                </Flex>
              </Flex>
            ) : undefined
          }
        />
      )}

      <Card title={`${t('counts.stillToCount')} (${String(toCount.length)})`}>
        {toCount.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('counts.nothingLeft')} />
        ) : (
          <List
            dataSource={toCount}
            renderItem={(l) => (
              <List.Item key={l.uid}>
                <Flex vertical>
                  <Text strong className="tabular">
                    {l.skuCode}
                  </Text>
                  <Text type="secondary">{l.name}</Text>
                </Flex>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Card title={`${t('counts.counted')} (${String(counted.length)})`}>
        {counted.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('counts.nothingCounted')} />
        ) : (
          <List
            dataSource={counted}
            renderItem={(l) => (
              <List.Item
                key={l.uid}
                actions={
                  l.addedOnShelf
                    ? [
                        <Button
                          key="remove"
                          aria-label={t('counts.remove', { code: l.skuCode })}
                          icon={<DeleteOutlined />}
                          size="large"
                          loading={remove.isPending && remove.variables === l.uid}
                          onClick={() => {
                            remove.mutate(l.uid);
                          }}
                        />,
                      ]
                    : []
                }
              >
                <Flex vertical>
                  <Text strong className="tabular">
                    {l.skuCode}
                  </Text>
                  <Text type="secondary">{l.name}</Text>
                  {l.addedOnShelf && <Tag>{t('counts.addedOnShelf')}</Tag>}
                </Flex>
                <Text strong className="tabular" style={{ fontSize: 18 }}>
                  {l.countedQty}
                </Text>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Flex vertical gap={8}>
        <Button
          type="primary"
          size="large"
          block
          style={{ height: 52 }}
          loading={submit.isPending}
          disabled={!view.canSubmit}
          onClick={() => {
            submit.mutate();
          }}
        >
          {t('counts.submit')}
        </Button>
        {view.canCancel && (
          <Button
            danger
            size="large"
            block
            onClick={() => {
              setCancelling(true);
            }}
          >
            {t('counts.cancel')}
          </Button>
        )}
      </Flex>

      <CancelDrawer
        view={view}
        open={cancelling}
        onClose={() => {
          setCancelling(false);
          codeRef.current?.focus();
        }}
        onChange={onChange}
      />
    </Flex>
  );
}

function ReviewMode({ view, onChange }: ModeProps): ReactNode {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [approveReason, setApproveReason] = useState('');
  const [backReason, setBackReason] = useState('');
  // One key per drawer open, so a retry after a dropped connection cannot adjust stock twice.
  const approveKey = useRef<string>('');

  const showSystem = !view.blind && view.totals !== undefined;
  const approved = view.status === 'APPROVED';

  const lines = [...view.lines].sort((a, b) => Number(hasDifference(b)) - Number(hasDifference(a)));
  const changed = view.lines.filter(hasDifference);
  const up = changed.filter((l) => !(l.difference ?? '').startsWith('-')).length;
  const down = changed.length - up;

  const approve = useMutation({
    mutationFn: () => {
      const reason = approveReason.trim();
      return approveStockCount(view.uid, reason.length > 0 ? { reason } : {}, approveKey.current);
    },
    onSuccess: (next) => {
      setApproving(false);
      onChange(next);
    },
  });

  const sendBack = useMutation({
    mutationFn: () => sendBackStockCount(view.uid, { reason: backReason.trim() }),
    onSuccess: (next) => {
      setSendingBack(false);
      setBackReason('');
      onChange(next);
    },
  });

  const differences = useQuery({
    queryKey: ['stock-count-autocount', view.uid],
    queryFn: () => getAutocountDifferences(view.uid),
    enabled: approved && view.totals !== undefined,
  });

  const download = async (): Promise<void> => {
    try {
      const csv = await exportAutocountDifferencesCsv(view.uid);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `autocount-differences-${view.countNo}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t('counts.actionFailed'));
    }
  };

  const timeline = [
    { children: t('counts.timeline.created', { who: orDash(view.createdBy), at: formatDateTime(view.createdAt) }) },
    ...(view.submittedAt !== null
      ? [{ children: t('counts.timeline.submitted', { who: orDash(view.submittedBy), at: formatDateTime(view.submittedAt) }) }]
      : []),
    ...(view.approvedAt !== null
      ? [{ color: 'green', children: t('counts.timeline.approved', { who: orDash(view.approvedBy), at: formatDateTime(view.approvedAt) }) }]
      : []),
    ...(view.status === 'CANCELLED'
      ? [{ color: 'red', children: t('counts.timeline.cancelled', { reason: orDash(view.cancelReason) }) }]
      : []),
  ];

  const columns = [
    {
      title: t('counts.review.part'),
      key: 'part',
      render: (_: unknown, l: StockCountLineView) => (
        <Flex vertical>
          <Text strong className="tabular">
            {l.skuCode}
          </Text>
          <Text type="secondary">{l.name}</Text>
          {l.belowReserved === true && <Tag color="orange">{t('counts.belowReserved')}</Tag>}
        </Flex>
      ),
    },
    ...(showSystem
      ? [
          {
            title: t('counts.review.system'),
            key: 'system',
            align: 'right' as const,
            render: (_: unknown, l: StockCountLineView) => <span className="tabular">{orDash(l.systemQty)}</span>,
          },
        ]
      : []),
    {
      title: t('counts.review.counted'),
      key: 'counted',
      align: 'right' as const,
      render: (_: unknown, l: StockCountLineView) => (
        <span className="tabular">{l.countedQty ?? t('counts.review.notCounted')}</span>
      ),
    },
    ...(showSystem
      ? [
          {
            title: t('counts.review.difference'),
            key: 'difference',
            align: 'right' as const,
            render: (_: unknown, l: StockCountLineView) => (
              <span className="tabular" style={{ fontWeight: hasDifference(l) ? 600 : 400 }}>
                {orDash(l.difference)}
              </span>
            ),
          },
          {
            title: t('counts.review.value'),
            key: 'value',
            align: 'right' as const,
            render: (_: unknown, l: StockCountLineView) => (
              <span className="tabular">
                {l.differenceValue === undefined || l.differenceValue === null ? '—' : formatMoney(l.differenceValue)}
              </span>
            ),
          },
          {
            title: t('counts.review.autocount'),
            key: 'autocount',
            align: 'right' as const,
            render: (_: unknown, l: StockCountLineView) => (
              <Flex vertical align="flex-end">
                <span className="tabular">{orDash(l.autocountQty)}</span>
                {l.autocountReadAt !== undefined && l.autocountReadAt !== null && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatDateTime(l.autocountReadAt)}
                  </Text>
                )}
              </Flex>
            ),
          },
        ]
      : []),
  ];

  return (
    <Flex vertical gap={16}>
      <Card>
        <Flex vertical gap={12}>
          <Flex justify="space-between" align="flex-start" gap={12} wrap>
            <Flex vertical>
              <Title level={4} className="tabular" style={{ margin: 0 }}>
                {view.countNo}
              </Title>
              <Text type="secondary">{view.locationName}</Text>
            </Flex>
            <Tag color={STATUS_COLOUR[view.status]}>{t(`counts.status.${view.status}`)}</Tag>
          </Flex>
          <Timeline items={timeline} style={{ marginBottom: -24 }} />
        </Flex>
      </Card>

      {view.totals !== undefined && (
        <Card>
          <Flex gap={24} wrap>
            <Statistic title={t('counts.totalsUp')} value={view.totals.up} />
            <Statistic title={t('counts.totalsDown')} value={view.totals.down} />
            <Statistic title={t('counts.totalsValueUp')} value={formatMoney(view.totals.valueUp)} />
            <Statistic title={t('counts.totalsValueDown')} value={formatMoney(view.totals.valueDown)} />
          </Flex>
        </Card>
      )}

      <Flex gap={8} wrap>
        {view.canApprove && (
          <Button
            type="primary"
            size="large"
            onClick={() => {
              approveKey.current = crypto.randomUUID();
              setApproving(true);
            }}
          >
            {t('counts.approve')}
          </Button>
        )}
        {view.canSendBack && (
          <Button
            size="large"
            onClick={() => {
              setSendingBack(true);
            }}
          >
            {t('counts.sendBack')}
          </Button>
        )}
        {view.canCancel && (
          <Button
            danger
            size="large"
            onClick={() => {
              setCancelling(true);
            }}
          >
            {t('counts.cancel')}
          </Button>
        )}
      </Flex>

      <Table<StockCountLineView>
        rowKey="uid"
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={lines}
        columns={columns}
      />

      {approved && view.totals !== undefined && (
        <Card
          title={t('counts.autocountTitle')}
          extra={
            <Button
              onClick={() => {
                void download();
              }}
            >
              {t('counts.downloadCsv')}
            </Button>
          }
        >
          <Flex vertical gap={12}>
            <Text type="secondary">{t('counts.autocountHelp')}</Text>
            <Table
              rowKey="skuCode"
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              loading={differences.isPending}
              locale={{ emptyText: t('counts.autocountEmpty') }}
              dataSource={differences.data ?? []}
              columns={[
                { title: t('counts.review.part'), key: 'part', render: (_: unknown, r) => (
                  <Flex vertical>
                    <Text strong className="tabular">{r.skuCode}</Text>
                    <Text type="secondary">{r.name}</Text>
                  </Flex>
                ) },
                { title: t('counts.autocountColumns.location'), dataIndex: 'autocountLocationCode', render: (v: string | null) => orDash(v) },
                { title: t('counts.autocountColumns.counted'), dataIndex: 'countedQty', align: 'right', render: (v: string) => <span className="tabular">{v}</span> },
                { title: t('counts.autocountColumns.autocount'), dataIndex: 'autocountQty', align: 'right', render: (v: string) => <span className="tabular">{v}</span> },
                { title: t('counts.autocountColumns.read'), dataIndex: 'autocountReadAt', render: (v: string | null) => formatDateTime(v) },
              ]}
            />
          </Flex>
        </Card>
      )}

      <ActionDrawer
        open={approving}
        title={t('counts.approveTitle')}
        submitLabel={t('counts.approveSubmit')}
        onClose={() => {
          setApproving(false);
        }}
        onSubmit={() => {
          approve.mutate();
        }}
        submitting={approve.isPending}
        consequence={
          changed.length === 0
            ? t('counts.approveNoChange')
            : t('counts.approveSummary', {
                n: changed.length,
                up,
                down,
                valueUp: formatMoney(view.totals?.valueUp ?? '0'),
                valueDown: formatMoney(view.totals?.valueDown ?? '0'),
              })
        }
        error={
          approve.isError ? (
            <Alert type="error" showIcon message={errorText(approve.error, t('counts.actionFailed'))} />
          ) : undefined
        }
      >
        <label htmlFor="count-approve-reason">{t('counts.approveReason')}</label>
        <Input.TextArea
          id="count-approve-reason"
          rows={3}
          value={approveReason}
          onChange={(e) => {
            setApproveReason(e.target.value);
          }}
        />
      </ActionDrawer>

      <ActionDrawer
        open={sendingBack}
        title={t('counts.sendBackTitle')}
        submitLabel={t('counts.sendBackTitle')}
        consequence={t('counts.sendBackConsequence')}
        onClose={() => {
          setSendingBack(false);
        }}
        onSubmit={() => {
          sendBack.mutate();
        }}
        submitting={sendBack.isPending}
        submitDisabled={backReason.trim().length === 0}
        error={
          sendBack.isError ? (
            <Alert type="error" showIcon message={errorText(sendBack.error, t('counts.actionFailed'))} />
          ) : undefined
        }
      >
        <label htmlFor="count-back-reason">{t('counts.reason')}</label>
        <Input.TextArea
          id="count-back-reason"
          rows={3}
          value={backReason}
          placeholder={t('counts.reasonRequired')}
          onChange={(e) => {
            setBackReason(e.target.value);
          }}
        />
      </ActionDrawer>

      <CancelDrawer
        view={view}
        open={cancelling}
        onClose={() => {
          setCancelling(false);
        }}
        onChange={onChange}
      />
    </Flex>
  );
}
